import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdChevronLeft, MdChevronRight, MdSchedule, MdPerson, MdGroups, MdEventBusy, MdInfoOutline,
} from 'react-icons/md';
import * as sessionService from '../../services/sessionService';
import { getClasses } from '../../services/classService';
import AttendanceModal from '../../components/attendance/AttendanceModal';
import DualDatePicker from '../../components/common/DualDatePicker';
import useClubVocab from '../../hooks/useClubVocab';
import useAuth from '../../hooks/useAuth';
import useTheme from '../../context/ThemeContext';
import { formatAdNice, formatBsNice } from '../../utils/calendar';

// Local-calendar "YYYY-MM-DD" (avoids the UTC day-shift of toISOString in +tz).
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// ── One class card ───────────────────────────────────────────────────────────
function ClassCard({ session, v, freeForm = false, removed = false, onOpen }) {
  const currentTheme = useTheme()?.currentTheme;
  const taken = session.attendanceTaken;

  // Breakdown (present · late · absent) only for a card that's been marked.
  const { data: summary } = useQuery({
    queryKey: ['attendance-summary', session._id],
    queryFn:  () => sessionService.getAttendanceSummary(session._id),
    enabled:  taken,
    staleTime: 0,
  });
  const counts = useMemo(() => {
    const list = summary?.students || [];
    return {
      present: list.filter(s => s.status === 'present').length,
      late:    list.filter(s => s.status === 'late').length,
      absent:  list.filter(s => s.status === 'absent').length,
    };
  }, [summary]);

  const title       = freeForm ? session.className : (session.classId?.name || session.className);
  const trainerName = session.substitutedBy?.name || session.trainerId?.name;
  const timeRange   = [fmtTime(session.startTime), fmtTime(session.endTime)].filter(Boolean).join(' – ');

  const isDark = currentTheme === 'dark';
  // Small state pills: subtle alpha tints in dark, light chips in light — legible in both.
  const pills = isDark
    ? { present: 'bg-green-500/15 text-green-300', late: 'bg-amber-500/15 text-amber-400', absent: 'bg-red-500/15 text-red-300' }
    : { present: 'bg-green-100 text-green-700',    late: 'bg-amber-500/15 text-amber-400', absent: 'bg-red-100 text-red-700' };
  // App-canonical themed primary (only true CTAs go red): brand red in dark, black/white in light.
  const primaryBtn = isDark ? 'bg-primary text-white shadow-sm' : 'bg-secondary text-card';

  return (
    <div className="bg-card border border-divider rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 min-w-0">
        <p className="font-bold text-secondary text-base truncate">{title}</p>
        {removed && (
          <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-nav-hover text-subtle">
            Class removed
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-subtle">
        {timeRange && <span className="flex items-center gap-1"><MdSchedule className="text-sm" /> {timeRange}</span>}
        {trainerName && <span className="flex items-center gap-1"><MdPerson className="text-sm" /> {trainerName}</span>}
        {session.studentCount != null && (
          <span className="flex items-center gap-1"><MdGroups className="text-sm" /> {session.studentCount} {v.members.toLowerCase()}</span>
        )}
      </div>

      <div className="mt-4">
        {taken ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pills.present}`}>{counts.present} P</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pills.late}`}>{counts.late} L</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pills.absent}`}>{counts.absent} A</span>
            </div>
            <button
              onClick={onOpen}
              className="px-5 py-2 rounded-full text-sm font-semibold border border-divider text-secondary hover:bg-nav-hover transition-colors"
            >
              Edit
            </button>
          </div>
        ) : (
          <button
            onClick={onOpen}
            className={`w-full py-3 rounded-full font-semibold text-sm ${primaryBtn} hover:opacity-90 transition-all active:scale-[0.99]`}
          >
            Take Attendance
          </button>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
// Shared by admin (/admin/attendance) and trainer (/trainer/attendance via trainerOnly). The
// day-list endpoint already materializes today's/past classes into real sessions and (for
// trainers) filters to their own — so tapping a card opens the marking grid directly. No "session"
// wording anywhere; free-form one-offs sit under a subtle "Other" group so nothing is unreachable.
function AttendanceDay({ trainerOnly = false }) {
  document.title = 'Attendance — TrainAxis';
  const v = useClubVocab();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = !trainerOnly;
  // Only the Primary Admin may reach past dates beyond yesterday (backend enforces this too).
  const isPrimary = isAdmin && (user?.isPrimaryAdmin === true || user?.adminType === 'primary');

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const TODAY = toISO(today);
  const yesterdayISO = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() - 1); return toISO(d); }, [today]);
  // Window: Primary Admin → any past day (min effectively unbounded; max=today guards the future);
  // staff admins → today+yesterday; trainers → today only. Never future for anyone.
  const minISO = isPrimary ? '2000-01-01' : (isAdmin ? yesterdayISO : TODAY);
  const maxISO = TODAY;

  const [dateISO, setDateISO] = useState(TODAY);
  const [active,  setActive]  = useState(null); // the class record being marked
  const viewedDate = useMemo(() => new Date(`${dateISO}T00:00:00`), [dateISO]);

  const { data, isPending } = useQuery({
    queryKey: ['sessions', dateISO],
    queryFn:  () => sessionService.getSessions({ date: dateISO }),
    staleTime: 0,
  });
  const rows = data?.sessions || [];
  const live = rows.filter(s => !s.upcoming && s.status !== 'cancelled');
  // Live scheduled classes as primary cards. Free-form one-offs AND removed-class history (marked
  // attendance for a since-deleted class) drop to "Other" so nothing valid disappears.
  const classCards = live.filter(s => s.classId && !s.classRemoved);
  const otherCards = live.filter(s => !s.classId || s.classRemoved);

  // Why-are-classes-missing feedback (admin): active classes scheduled this weekday with NO trainer
  // never materialize into sessions (trainerless skip). Surface the count so the owner can fix it.
  const { data: classesData } = useQuery({
    queryKey: ['classes-active-min'],
    queryFn:  () => getClasses({ isActive: 'true' }).then(r => r.data),
    enabled:  isAdmin,
    staleTime: 5 * 60 * 1000,
  });
  const viewedWeekday = WEEKDAY_NAMES[viewedDate.getDay()];
  const missingTrainerCount = isAdmin
    ? (classesData?.classes || classesData?.data || []).filter(c => (c.days || []).includes(viewedWeekday) && !c.trainer).length
    : 0;

  const shift = (delta) => {
    const d = new Date(viewedDate); d.setDate(d.getDate() + delta);
    const iso = toISO(d);
    if (iso < minISO || iso > maxISO) return;
    setDateISO(iso);
  };
  const canPrev = dateISO > minISO;
  const canNext = dateISO < maxISO;
  const isToday = dateISO === TODAY;

  const arrowCls = 'w-10 h-10 flex items-center justify-center rounded-full border border-divider text-secondary disabled:opacity-30 disabled:cursor-not-allowed hover:bg-nav-hover transition-colors';

  return (
    <div className="max-w-2xl mx-auto pb-10">
      {/* Header */}
      <h1 className="text-xl font-bold text-secondary">Attendance</h1>
      <p className="text-muted text-sm mt-0.5">Tap a class to mark who showed up.</p>

      <div className="mt-4 flex items-center gap-2">
        <button onClick={() => shift(-1)} disabled={!canPrev} className={arrowCls} aria-label="Previous day">
          <MdChevronLeft className="text-2xl" />
        </button>
        <div className="flex-1 text-center min-w-0">
          <p className="text-sm font-bold text-secondary truncate">{formatBsNice(viewedDate)}</p>
          <p className="text-xs text-subtle truncate">{formatAdNice(viewedDate)}</p>
        </div>
        <button onClick={() => shift(1)} disabled={!canNext} className={arrowCls} aria-label="Next day">
          <MdChevronRight className="text-2xl" />
        </button>
      </div>

      {/* Primary Admin: jump straight to any past date instead of arrowing back day by day. */}
      {isPrimary && (
        <div className="mt-3 max-w-[240px] mx-auto">
          <DualDatePicker
            label="Jump to a past date"
            value={dateISO}
            onChange={(iso) => { if (iso && iso >= minISO && iso <= maxISO) setDateISO(iso); }}
            max={TODAY}
            fastNav
          />
        </div>
      )}

      {!isToday && (
        <div className="text-center mt-2">
          <button onClick={() => setDateISO(TODAY)} className="text-xs text-primary font-semibold hover:underline">
            Jump to Today
          </button>
        </div>
      )}

      {/* Body */}
      <div className="mt-5">
        {isPending ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-28 bg-nav-hover rounded-2xl animate-pulse" />)}
          </div>
        ) : (classCards.length === 0 && otherCards.length === 0 && missingTrainerCount === 0) ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-nav-hover flex items-center justify-center mx-auto mb-4">
              <MdEventBusy className="text-3xl text-subtle" />
            </div>
            <p className="text-sm font-semibold text-muted">
              No classes scheduled {isToday ? 'today' : 'for this day'}.
            </p>
            {isAdmin && (
              <Link to="/admin/classes" className="inline-block mt-3 text-primary text-sm font-semibold hover:underline">
                Manage classes
              </Link>
            )}
          </div>
        ) : (
          <>
            {classCards.length > 0 && (
              <div className="space-y-3">
                {classCards.map(s => <ClassCard key={s._id} session={s} v={v} onOpen={() => setActive(s)} />)}
              </div>
            )}

            {otherCards.length > 0 && (
              <div className="mt-7">
                <p className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em] mb-2 px-1">Other</p>
                <div className="space-y-3">
                  {otherCards.map(s => (
                    <ClassCard key={s._id} session={s} v={v}
                      freeForm={!s.classId} removed={s.classRemoved} onOpen={() => setActive(s)} />
                  ))}
                </div>
              </div>
            )}

            {/* Subtle info: classes that couldn't appear because they have no trainer (admin only) */}
            {missingTrainerCount > 0 && (
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-divider bg-nav-hover/50 px-4 py-3.5">
                <MdInfoOutline className="text-lg text-subtle flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm text-secondary">
                    {missingTrainerCount} class{missingTrainerCount !== 1 ? 'es' : ''} scheduled {isToday ? 'today' : 'this day'} couldn&apos;t appear because {missingTrainerCount !== 1 ? 'they have' : 'it has'} no trainer assigned.
                  </p>
                  <Link to="/admin/classes" className="text-primary text-xs font-semibold hover:underline">Fix in Classes →</Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {active && (
        <AttendanceModal
          session={active}
          onClose={() => setActive(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['sessions', dateISO] });
            qc.invalidateQueries({ queryKey: ['attendance-summary', active._id] });
            setActive(null);
          }}
        />
      )}
    </div>
  );
}

export default AttendanceDay;
